import { AuthContextProps } from 'react-oidc-context';

/**
 * Microsoft claims-based role property key
 */
export const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

/**
 * Common role names in the PLI system
 */
export const ROLES = {
  WEBEDITORS: 'WebEditors',
  EDITOR: 'Editor',
  FACULTY: 'Faculty',
  WEB_ADMINS: 'WebAdmins',
  LMS_ADMIN: 'LMSAdmin'
} as const;

/**
 * Check if a user has a specific role
 * @param auth - The authentication context from useAuth()
 * @param roleName - The role name to check for
 * @returns true if the user has the specified role
 */
export const hasRole = (auth: AuthContextProps | undefined, roleName: string): boolean => {
  // Check if user is authenticated
  if (!auth?.isAuthenticated || !auth.user?.profile) {
    return false;
  }

  // Check if access token is expired
  if (auth.user?.expired) {
    console.warn('Access token is expired. Roles may not be valid.');
    return false;
  }

  const profile = auth.user.profile;

  // Check for role in the Microsoft claims property
  const roles = profile[ROLE_CLAIM];

  if (typeof roles === 'string') {
    return roles === roleName;
  }

  if (Array.isArray(roles)) {
    return roles.includes(roleName);
  }

  return false;
};

/**
 * Check if user has WEBEDITORS role
 * @param auth - The authentication context from useAuth()
 * @returns true if the user has the WEBEDITORS role
 */
export const isWebEditors = (auth: AuthContextProps | undefined): boolean => {
  return hasRole(auth, ROLES.WEBEDITORS);
};

/**
 * Get all roles for the current user
 * @param auth - The authentication context from useAuth()
 * @returns Array of role names, or empty array if no roles
 */
export const getUserRoles = (auth: AuthContextProps | undefined): string[] => {
  if (!auth?.isAuthenticated || !auth.user?.profile) {
    return [];
  }

  const profile = auth.user.profile;
  const roles = profile[ROLE_CLAIM];

  if (typeof roles === 'string') {
    return [roles];
  }

  if (Array.isArray(roles)) {
    return roles;
  }

  return [];
};

/**
 * Check if user has any of the specified roles
 * @param auth - The authentication context from useAuth()
 * @param roleNames - Array of role names to check
 * @returns true if the user has at least one of the specified roles
 */
export const hasAnyRole = (auth: AuthContextProps | undefined, roleNames: string[]): boolean => {
  const userRoles = getUserRoles(auth);
  return roleNames.some(role => userRoles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param auth - The authentication context from useAuth()
 * @param roleNames - Array of role names to check
 * @returns true if the user has all of the specified roles
 */
export const hasAllRoles = (auth: AuthContextProps | undefined, roleNames: string[]): boolean => {
  const userRoles = getUserRoles(auth);
  return roleNames.every(role => userRoles.includes(role));
};
